import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export type BaseMap = 'streets' | 'clean' | 'night' | 'satellite';

export interface MapMarker {
  id: number | string;
  latitude: number;
  longitude: number;
  course?: number;
  label?: string;
  /** Drives the marker colour: moving, parked, or stale. */
  tone?: 'moving' | 'parked' | 'offline';
  selected?: boolean;
}

interface Props {
  markers?: MapMarker[];
  /** Polyline for trip replay. Speed drives the colour of each segment. */
  track?: TrackSegmentPoint[];
  /** The animated marker that walks the track during playback. */
  playhead?: { latitude: number; longitude: number; course: number } | null;
  userLocation?: { latitude: number; longitude: number } | null;
  onMarkerClick?: (id: number | string) => void;
  /** Change this value to re-fit the view to whatever is on the map. */
  fitKey?: string | number;
  /** Keep this marker centred as it moves. */
  followId?: number | string | null;
  basemap?: BaseMap;
  className?: string;
}

const TILES: Record<BaseMap, { url: string; attribution: string; maxZoom: number }> = {
  streets: {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap',
    maxZoom: 19,
  },
  clean: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Esri, HERE, Garmin, &copy; OpenStreetMap contributors',
    maxZoom: 16,
  },
  night: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Esri, HERE, Garmin, &copy; OpenStreetMap contributors',
    maxZoom: 16,
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics',
    maxZoom: 19,
  },
};

/*
 * A note on tiles, because this has already bitten once.
 *
 * The light and night maps used CARTO, which serves an "API KEY REQUIRED"
 * watermark once an anonymous caller passes its quota — the map still loads,
 * it just becomes unreadable, which is worse than failing.
 *
 * Every keyless tile service has limits like this, and none of them promise
 * anything to a commercial product. Once there are real customers browsing
 * maps daily, buy a key (MapTiler and Thunderforest both have free tiers and
 * paid plans) and change the four URLs above. Nothing else has to move.
 */

export interface TrackSegmentPoint {
  latitude: number;
  longitude: number;
  speedKph: number;
}

/**
 * Speed bands for the replay route. Chosen around Egyptian limits: city,
 * main road, highway, then over.
 */
export const SPEED_BANDS: Array<{ upTo: number; colour: string }> = [
  { upTo: 5, colour: '#94a3b8' },
  { upTo: 40, colour: '#10b981' },
  { upTo: 70, colour: '#84cc16' },
  { upTo: 100, colour: '#f59e0b' },
  { upTo: 130, colour: '#f97316' },
  { upTo: Infinity, colour: '#ef4444' },
];

function bandIndex(speedKph: number): number {
  return SPEED_BANDS.findIndex((band) => speedKph <= band.upTo);
}

const TONE_COLOUR: Record<string, string> = {
  moving: '#10b981',
  parked: '#3b82f6',
  offline: '#94a3b8',
};

/**
 * A vehicle arrow that points where the car is heading.
 * Built as a divIcon so it can be rotated and recoloured without image assets,
 * which also sidesteps the broken-default-icon problem bundlers have with Leaflet.
 */
function vehicleIcon(marker: MapMarker): L.DivIcon {
  const colour = TONE_COLOUR[marker.tone ?? 'parked'] ?? TONE_COLOUR.parked;
  const rotation = marker.course ?? 0;

  const pulse =
    marker.tone === 'moving'
      ? `<circle class="marker-pulse" cx="22" cy="22" r="10" fill="${colour}" opacity="0.4" />`
      : '';
  const ring = marker.selected
    ? `<circle cx="22" cy="22" r="17" fill="none" stroke="${colour}" stroke-width="2.5" opacity="0.9" />`
    : '';

  return L.divIcon({
    className: 'vehicle-marker',
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    html: `
      <svg width="44" height="44" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg">
        ${pulse}${ring}
        <g transform="rotate(${rotation} 22 22)">
          <path d="M22 8 L31 33 L22 27.5 L13 33 Z"
                fill="${colour}" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round" />
        </g>
      </svg>`,
  });
}

function userIcon(): L.DivIcon {
  return L.divIcon({
    className: 'user-marker',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    html: `
      <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
        <circle class="marker-pulse" cx="11" cy="11" r="6" fill="#f59e0b" opacity="0.45" />
        <circle cx="11" cy="11" r="6" fill="#f59e0b" stroke="#ffffff" stroke-width="3" />
      </svg>`,
  });
}

/** Bookkeeping for gliding a marker from its old position to a new one. */
interface Animated {
  marker: L.Marker;
  from: L.LatLng;
  to: L.LatLng;
  start: number;
  duration: number;
  frame: number | null;
}

export function MapView({
  markers = [],
  track,
  playhead,
  userLocation,
  onMarkerClick,
  fitKey,
  followId,
  basemap = 'clean',
  className,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileRef = useRef<L.TileLayer | null>(null);
  const markerLayer = useRef<Map<string, Animated>>(new Map());
  const trackLine = useRef<L.LayerGroup | null>(null);
  const trackGlow = useRef<L.Polyline | null>(null);
  const endCaps = useRef<L.LayerGroup | null>(null);
  const playheadMarker = useRef<L.Marker | null>(null);
  const userMarker = useRef<L.Marker | null>(null);
  const followRef = useRef(followId);
  followRef.current = followId;

  // Create the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [30.0444, 31.2357],
      zoom: 11,
      zoomControl: true,
      attributionControl: true,
      // Smooth out wheel zoom so panning around a fleet feels less jumpy.
      wheelPxPerZoomLevel: 120,
    });

    mapRef.current = map;
    // Leaflet mis-measures when created inside a flex/grid layout that has not
    // settled; one deferred invalidate fixes grey tiles on first paint.
    const timer = setTimeout(() => map.invalidateSize(), 0);

    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(containerRef.current);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      for (const entry of markerLayer.current.values()) {
        if (entry.frame) cancelAnimationFrame(entry.frame);
      }
      markerLayer.current.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Swap the basemap without tearing down the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    tileRef.current?.remove();
    const config = TILES[basemap];
    tileRef.current = L.tileLayer(config.url, {
      attribution: config.attribution,
      maxZoom: config.maxZoom,
    }).addTo(map);
    tileRef.current.bringToBack();
  }, [basemap]);

  // Sync vehicle markers. Existing markers glide to their new position rather
  // than teleporting, which is what makes live tracking read as movement.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const seen = new Set<string>();

    for (const marker of markers) {
      const key = String(marker.id);
      seen.add(key);
      const target = L.latLng(marker.latitude, marker.longitude);
      const existing = markerLayer.current.get(key);

      if (!existing) {
        const created = L.marker(target, { icon: vehicleIcon(marker) }).addTo(map);
        if (marker.label) created.bindTooltip(marker.label, { direction: 'top', offset: [0, -20] });
        created.on('click', () => onMarkerClick?.(marker.id));
        markerLayer.current.set(key, {
          marker: created,
          from: target,
          to: target,
          start: 0,
          duration: 0,
          frame: null,
        });
        continue;
      }

      existing.marker.setIcon(vehicleIcon(marker));
      if (marker.label) existing.marker.setTooltipContent(marker.label);

      const current = existing.marker.getLatLng();
      if (current.equals(target)) continue;

      // A tracker reporting every few seconds looks best glided over ~900ms.
      // Anything further than a few hundred metres is a jump, not travel: snap.
      if (current.distanceTo(target) > 800) {
        existing.marker.setLatLng(target);
        continue;
      }

      if (existing.frame) cancelAnimationFrame(existing.frame);
      existing.from = current;
      existing.to = target;
      existing.start = performance.now();
      existing.duration = 900;

      const step = (now: number) => {
        const progress = Math.min(1, (now - existing.start) / existing.duration);
        // easeOutCubic keeps the arrival gentle.
        const eased = 1 - (1 - progress) ** 3;
        const lat = existing.from.lat + (existing.to.lat - existing.from.lat) * eased;
        const lng = existing.from.lng + (existing.to.lng - existing.from.lng) * eased;
        existing.marker.setLatLng([lat, lng]);

        if (followRef.current !== undefined && String(followRef.current) === key) {
          map.panTo([lat, lng], { animate: false });
        }

        existing.frame = progress < 1 ? requestAnimationFrame(step) : null;
      };
      existing.frame = requestAnimationFrame(step);
    }

    for (const [key, entry] of markerLayer.current) {
      if (!seen.has(key)) {
        if (entry.frame) cancelAnimationFrame(entry.frame);
        entry.marker.remove();
        markerLayer.current.delete(key);
      }
    }
  }, [markers, onMarkerClick]);

  // Sync the replay route: a soft glow underneath, then one polyline per run of
  // consecutive fixes in the same speed band, then start/end caps.
  //
  // Drawing a separate line per point would be thousands of layers on a month
  // of history; banding collapses that to a few dozen while still showing where
  // the vehicle was slow and where it was fast.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    trackGlow.current?.remove();
    trackLine.current?.remove();
    endCaps.current?.remove();
    trackGlow.current = null;
    trackLine.current = null;
    endCaps.current = null;

    if (!track || track.length < 2) return;

    const latLngs = track.map((p) => [p.latitude, p.longitude] as [number, number]);

    trackGlow.current = L.polyline(latLngs, {
      color: '#6366f1',
      weight: 13,
      opacity: 0.14,
      lineCap: 'round',
      lineJoin: 'round',
    }).addTo(map);

    const runs: L.Polyline[] = [];
    let runStart = 0;
    let runBand = bandIndex(track[0]!.speedKph);

    const flushRun = (endExclusive: number) => {
      // Include the boundary point so consecutive runs touch instead of gapping.
      const slice = latLngs.slice(runStart, Math.min(endExclusive + 1, latLngs.length));
      if (slice.length < 2) return;
      runs.push(
        L.polyline(slice, {
          color: SPEED_BANDS[runBand]?.colour ?? '#4f46e5',
          weight: 4.5,
          opacity: 0.95,
          lineCap: 'round',
          lineJoin: 'round',
        }),
      );
    };

    for (let i = 1; i < track.length; i++) {
      const band = bandIndex(track[i]!.speedKph);
      if (band !== runBand) {
        flushRun(i);
        runStart = i;
        runBand = band;
      }
    }
    flushRun(track.length - 1);

    trackLine.current = L.layerGroup(runs).addTo(map);

    const first = latLngs[0]!;
    const last = latLngs[latLngs.length - 1]!;
    endCaps.current = L.layerGroup([
      L.circleMarker(first, {
        radius: 7,
        color: '#ffffff',
        weight: 3,
        fillColor: '#10b981',
        fillOpacity: 1,
      }).bindTooltip('start'),
      L.circleMarker(last, {
        radius: 7,
        color: '#ffffff',
        weight: 3,
        fillColor: '#f43f5e',
        fillOpacity: 1,
      }).bindTooltip('end'),
    ]).addTo(map);
  }, [track]);

  // Sync the playback marker.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!playhead) {
      playheadMarker.current?.remove();
      playheadMarker.current = null;
      return;
    }

    const icon = vehicleIcon({
      id: 'playhead',
      latitude: playhead.latitude,
      longitude: playhead.longitude,
      course: playhead.course,
      tone: 'moving',
      selected: true,
    });

    if (playheadMarker.current) {
      playheadMarker.current.setLatLng([playhead.latitude, playhead.longitude]);
      playheadMarker.current.setIcon(icon);
    } else {
      playheadMarker.current = L.marker([playhead.latitude, playhead.longitude], {
        icon,
        zIndexOffset: 1000,
      }).addTo(map);
    }
  }, [playhead]);

  // Sync the viewer's own position.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!userLocation) {
      userMarker.current?.remove();
      userMarker.current = null;
      return;
    }

    const position: L.LatLngExpression = [userLocation.latitude, userLocation.longitude];
    if (userMarker.current) {
      userMarker.current.setLatLng(position);
    } else {
      userMarker.current = L.marker(position, { icon: userIcon(), zIndexOffset: 900 }).addTo(map);
    }
  }, [userLocation]);

  // Fit the view when asked, never on every position update: refitting
  // constantly would fight the user panning the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || fitKey === undefined) return;

    const points: L.LatLngExpression[] = [];
    if (track) {
      for (const p of track) points.push([p.latitude, p.longitude]);
    }
    for (const marker of markers) points.push([marker.latitude, marker.longitude]);
    if (userLocation) points.push([userLocation.latitude, userLocation.longitude]);

    if (points.length === 1) {
      map.flyTo(points[0]!, 15, { duration: 0.7 });
    } else if (points.length > 1) {
      map.flyToBounds(L.latLngBounds(points), { padding: [60, 60], maxZoom: 16, duration: 0.7 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);

  return <div ref={containerRef} className={className ?? 'map'} />;
}
